import React, { useState, useEffect } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { Input, Button, Toast } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import MultiPhotoGalleryView from '../../components/MultiPhotoGalleryView';
import { TmsCountedInput } from './TmsCountedInput';
import { getCurrentServerTimeMillis } from '../../../../utils/pdaCameraUtils';
import { WaypointType } from '../../../../enums/TmsEnum';
import { pdaOpenCamera, startPrepareCameraLocation, FirstPhotoWatermarkLocation } from '../../../../utils/appRuntime';
import { GET_ACCOUNT_BIND_WAREHOUSE, GetAccountBindWarehouseResponse } from '../../../../graphql/queries/warehouseQueries';

interface TmsArrivalFormProps {
  waypointType?: number;
  remark?: string;
  onRemarkChange?: (remark: string) => void;
  photos?: string[];
  onPhotosChange?: (photos: string[]) => void;
  sealPhotos?: string[];
  onSealPhotosChange?: (photos: string[]) => void;
  onSubmit?: (remark: string, photos: string[], sealPhotos: string[], firstPhotoLocation?: FirstPhotoWatermarkLocation | null) => Promise<void>;
}

export const TmsArrivalForm: React.FC<TmsArrivalFormProps> = ({
  waypointType,
  remark = '',
  onRemarkChange,
  photos = [],
  onPhotosChange,
  sealPhotos = [],
  onSealPhotosChange,
  onSubmit
}) => {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const { data: warehouseData } = useQuery<GetAccountBindWarehouseResponse>(GET_ACCOUNT_BIND_WAREHOUSE);

  const [photosArray, setPhotosArray] = useState<string[]>(photos);
  const [sealPhotosArray, setSealPhotosArray] = useState<string[]>(sealPhotos);
  const [remarkValue, setRemarkValue] = useState<string>(remark);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [firstPhotoLocation, setFirstPhotoLocation] = useState<FirstPhotoWatermarkLocation | null>(null);

  const isOrigin = waypointType === WaypointType.ORIGIN;

  // 同步外部传入的 props（仅在组件挂载时初始化一次，避免覆盖内部状态更新）
  useEffect(() => {
    setPhotosArray(photos);
    setSealPhotosArray(sealPhotos);
    setRemarkValue(remark);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    startPrepareCameraLocation();
  }, []);

  // 照片配置常量
  const kMaxSelectorPhotoLength = 3;
  const kMinSelectorPhotoLength = 1;

  // 处理备注变化
  const handleRemarkChange = (val: string) => {
    // 限制为50个字
    const limitedVal = val.slice(0, 50);
    setRemarkValue(limitedVal);
    onRemarkChange?.(limitedVal);
  };

  // 处理到达凭证多图画廊的连拍功能
  const handleMultiPhotoGalleryBurstCapture = async () => {
    const currentPhotoCount = photosArray.length;
    const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);
    const minSelectionCount = currentPhotoCount >= kMinSelectorPhotoLength ? 0 : kMinSelectorPhotoLength - currentPhotoCount;

    // 使用PDA专用相机，传递国际化文本
    const photoTitles = [
      t('pda.pages.tms.arrival.firstPhoto') || t('pda.pages.exception.firstPhoto') || '第一张照片',
      t('pda.pages.tms.arrival.secondPhoto') || t('pda.pages.exception.secondPhoto') || '第二张照片',
      t('pda.pages.tms.arrival.thirdPhoto') || t('pda.pages.exception.thirdPhoto') || '第三张照片'
    ];
    const serverTimeMillis = await getCurrentServerTimeMillis(apolloClient);

    await pdaOpenCamera(
      remainingPhotoCount,
      minSelectionCount,
      t('pda.pages.tms.arrival.photoTips') || '',
      photoTitles,
      t('pda.pages.tms.arrival.complete') || t('pda.pages.exception.complete') || '',
      async (newPhotos: string[], loc?: FirstPhotoWatermarkLocation | null) => {
        try {
          if (newPhotos && newPhotos.length > 0) {
              setPhotosArray(prevPhotos => {
                  const updated = [...prevPhotos, ...newPhotos];
                  onPhotosChange?.(updated);
                  return updated;
              });
          }
          if (loc != null) {
            setFirstPhotoLocation(loc);
          }
        } catch (error) {
          console.error('多图片处理失败:', error);
          Toast.show({ content: t('pda.pages.exception.multiPhotoProcessingFailed') || 'Photo processing failed', position: 'center' });
        }
      },
      {
        enableWatermark: true,
        ianaTimeZone: warehouseData?.warehouse?.timezoneLocation,
        serverTimeMillis
      }
    );
  };

  // 处理到达凭证删除照片
  const handleDeletePhoto = (index: number) => {
    const updatedPhotos = photosArray.filter((_, i) => i !== index);
    setPhotosArray(updatedPhotos);
    onPhotosChange?.(updatedPhotos);
  };

  // 处理封签照片连拍
  const handleSealPhotoBurstCapture = async () => {
    if (isOrigin) {
      return;
    }

    const currentPhotoCount = sealPhotosArray.length;
    const remainingPhotoCount = Math.max(0, kMaxSelectorPhotoLength - currentPhotoCount);
    const minSelectionCount = currentPhotoCount > 0 ? 0 : 1;

    const photoTitles = [
      t('pda.pages.tms.arrival.firstPhoto') || t('pda.pages.exception.firstPhoto') || '第一张照片',
      t('pda.pages.tms.arrival.secondPhoto') || t('pda.pages.exception.secondPhoto') || '第二张照片',
      t('pda.pages.tms.arrival.thirdPhoto') || t('pda.pages.exception.thirdPhoto') || '第三张照片'
    ];
    const serverTimeMillis = await getCurrentServerTimeMillis(apolloClient);

    await pdaOpenCamera(
      remainingPhotoCount,
      minSelectionCount,
      t('pda.pages.tms.arrival.photoTips') || '',
      photoTitles,
      t('pda.pages.tms.arrival.complete') || t('pda.pages.exception.complete') || '',
      async (newPhotos: string[]) => {
        try {
          if (newPhotos && newPhotos.length > 0) {
            setSealPhotosArray(prevPhotos => {
              const updated = [...prevPhotos, ...newPhotos];
              onSealPhotosChange?.(updated);
              return updated;
            });
          }
        } catch (error) {
          console.error('封签照片处理失败:', error);
          Toast.show({ content: t('pda.pages.exception.multiPhotoProcessingFailed') || 'Photo processing failed', position: 'center' });
        }
      },
      {
        enableWatermark: true,
        ianaTimeZone: warehouseData?.warehouse?.timezoneLocation,
        serverTimeMillis
      }
    );
  };

  // 处理封签照片删除
  const handleDeleteSealPhoto = (index: number) => {
    const updatedPhotos = sealPhotosArray.filter((_, i) => i !== index);
    setSealPhotosArray(updatedPhotos);
    onSealPhotosChange?.(updatedPhotos);
  };

  // 处理提交
  const handleSubmit = async () => {
    // 检查是否需要上传到达凭证图片（至少1张）
    // 始发站：只要求车辆到达凭证；途径站/终到站：车辆到达凭证 + 封签照片
    if (photosArray.length < 1 || (!isOrigin && sealPhotosArray.length < 1)) {
      Toast.show({
        content: t('pda.pages.exception.pleaseUploadPhotos') || 'Please upload photos',
        position: 'center',
        duration: 2000
      });
      return;
    }

    if (!onSubmit) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(remarkValue, photosArray, sealPhotosArray, firstPhotoLocation);
    } catch (error) {
      console.error('提交失败:', error);
      // 错误处理已在父组件中完成，这里只记录日志
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* 备注标题 */}
      <div className="text-base font-medium text-gray-700">
        {t('pda.pages.tms.arrival.remark')}
      </div>

      {/* 备注输入框 + 内部字数统计 */}
      <TmsCountedInput
        value={remarkValue}
        onChange={handleRemarkChange}
        placeholder={t('pda.pages.tms.arrival.remarkPlaceholder') as string}
      />

      {/* 上传到达凭证标题 */}
      <div className="text-base font-medium text-gray-700">
        {t('pda.pages.tms.arrival.uploadProof')}
      </div>

      {/* 照片画廊：到达凭证 */}
      <MultiPhotoGalleryView
        photos={photosArray}
        onDelete={handleDeletePhoto}
        onNativeBurstCapture={handleMultiPhotoGalleryBurstCapture}
        maxDisplayCount={kMaxSelectorPhotoLength}
      />

      {/* 上传到达封签标题 */}
      {!isOrigin && (
        <>
          <div className="text-base font-medium text-gray-700">
            {t('pda.pages.tms.arrival.uploadSeal')}
          </div>

          {/* 照片画廊：到达封签 */}
          <MultiPhotoGalleryView
            photos={sealPhotosArray}
            onDelete={handleDeleteSealPhoto}
            onNativeBurstCapture={handleSealPhotoBurstCapture}
            maxDisplayCount={kMaxSelectorPhotoLength}
          />
        </>
      )}

      {/* 提交按钮 */}
      <div className="mt-4">
        <Button
          block
          color="primary"
          size="large"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={isSubmitting || photosArray.length < 1 || (!isOrigin && sealPhotosArray.length < 1)}
        >
          {t('pda.common.submit') || 'Submit'}
        </Button>
      </div>
    </div>
  );
};
